import React, { useCallback, useMemo } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useClientExportFlow } from '../../hooks/useClientExportFlow';
import { formatBillingCurrency } from '../../utils/billingRound';
import {
  mergeAzureCostSections,
  type AzureLast3MonthsAnalysisResult,
  type AzureSubscriptionCostResult,
} from '../../types/azureCost';
import MsrReportSlide from './MsrReportSlide';
import MsrChartCapture from './MsrChartCapture';
import MsrObservationBox from './MsrObservationBox';
import KpiChartPanel from './charts/KpiChartPanel';
import AzureCostAreaChart from './charts/AzureCostAreaChart';
import AzureCost3MonthBarChart from './charts/AzureCost3MonthBarChart';
import AzureCost3MonthAnalysisTable from './charts/AzureCost3MonthAnalysisTable';
import { buildAzureCostObservations } from './azureCostObservations';
import {
  exportAzureCostSectionExcel,
  exportAzureCostSectionPdf,
  type AzureCostSectionKey,
} from './azureCostSectionExport';

function monthYearLabel(month: number, year: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface AzureCostSectionQueryState<T> {
  data: T | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onRetry?: () => void;
}

export interface MsrAzureCostSectionProps {
  customerId: string;
  customerName: string;
  month: number;
  year: number;
  dateFrom: string;
  dateTo: string;
  subscription: AzureCostSectionQueryState<AzureSubscriptionCostResult>;
  last3MonthsAnalysis: AzureCostSectionQueryState<AzureLast3MonthsAnalysisResult>;
}

function SubscriptionSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true">
      <div className="h-24 animate-pulse rounded-xl bg-slate-200/80" />
      <div className="h-[280px] animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="h-48 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
        <div className="h-48 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
      </div>
    </div>
  );
}

function ChartSkeleton({ height = 300 }: { height?: number }) {
  return (
    <div
      className="animate-pulse rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-100 to-slate-50"
      style={{ height }}
      aria-busy="true"
    />
  );
}

function SectionError({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="rounded-xl border border-red-200/80 bg-white p-6 text-center shadow-sm">
      <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-red-500" aria-hidden />
      <p className="mt-3 text-sm font-semibold text-red-900">{title}</p>
      <p className="mt-1 text-sm text-red-700/90">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

const MsrAzureCostSection: React.FC<MsrAzureCostSectionProps> = ({
  customerId,
  month,
  year,
  dateFrom,
  dateTo,
  subscription,
  last3MonthsAnalysis,
}) => {
  const { run, modal } = useClientExportFlow();
  const periodLabel = monthYearLabel(month, year);

  const mergedExportData = useMemo(
    () => mergeAzureCostSections(subscription.data, last3MonthsAnalysis.data),
    [subscription.data, last3MonthsAnalysis.data]
  );

  const hasSubscriptionData = Boolean(
    subscription.data && customerId && subscription.data.customerId === customerId
  );
  const hasAnalysisData = Boolean(
    last3MonthsAnalysis.data &&
      customerId &&
      last3MonthsAnalysis.data.customerId === customerId
  );

  const makeExportHandlers = useCallback(
    (section: AzureCostSectionKey) => ({
      onExportPdf: () => {
        if (!mergedExportData) return;
        void (async () => {
          try {
            const file = await exportAzureCostSectionPdf(
              section,
              mergedExportData,
              dateFrom,
              dateTo,
              month,
              year
            );
            await run({
              downloadButtonLabel: 'Download PDF',
              work: async () => ({
                files: file,
                summaryLines: ['Your PDF is ready to download.'],
              }),
            });
          } catch (e) {
            await run({
              downloadButtonLabel: 'Download PDF',
              work: async () => {
                throw e;
              },
            });
          }
        })();
      },
      onExportExcel: () => {
        if (!mergedExportData) return;
        void (async () => {
          try {
            const file = await exportAzureCostSectionExcel(
              section,
              mergedExportData,
              dateFrom,
              dateTo,
              month,
              year
            );
            await run({
              downloadButtonLabel: 'Download Excel',
              work: async () => ({
                files: file,
                summaryLines: ['Your Excel file is ready to download.'],
              }),
            });
          } catch (e) {
            await run({
              downloadButtonLabel: 'Download Excel',
              work: async () => {
                throw e;
              },
            });
          }
        })();
      },
    }),
    [mergedExportData, dateFrom, dateTo, month, year, run]
  );

  const subscriptionExport = useMemo(
    () => makeExportHandlers('azure-subscription'),
    [makeExportHandlers]
  );
  const threeMonthExport = useMemo(
    () => makeExportHandlers('azure-3months'),
    [makeExportHandlers]
  );
  const analysisExport = useMemo(
    () => makeExportHandlers('azure-3months-analysis'),
    [makeExportHandlers]
  );

  const subscriptionData = subscription.data;
  const analysisData = last3MonthsAnalysis.data;

  const last3MonthObservations = useMemo(() => {
    if (!hasAnalysisData || !analysisData) return [];
    return buildAzureCostObservations(
      analysisData.last3Months ?? [],
      analysisData.serviceCostDeltas ?? [],
      analysisData.currency
    );
  }, [hasAnalysisData, analysisData]);

  return (
    <>
      {modal}
      <MsrReportSlide
        id="msr-slide-azure-cost"
        title="Azure Subscription Cost Analysis"
        onExportPdf={subscriptionExport.onExportPdf}
        onExportExcel={subscriptionExport.onExportExcel}
        exportDisabled={subscription.isFetching || !hasSubscriptionData}
      >
        {subscription.isFetching && hasSubscriptionData ? (
          <div
            className="mb-3 flex items-center gap-1.5 text-xs font-medium text-indigo-800"
            aria-live="polite"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Updating subscription cost
          </div>
        ) : null}

        {subscription.isLoading ? (
          <SubscriptionSkeleton />
        ) : subscription.error && !hasSubscriptionData ? (
          <SectionError
            title="Azure subscription cost unavailable"
            message={subscription.error.message}
            onRetry={subscription.onRetry}
          />
        ) : subscriptionData ? (
          <>
            {subscription.error ? (
              <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
                <span className="min-w-0 flex-1">
                  Showing cached data. Latest refresh failed: {subscription.error.message}
                </span>
              </div>
            ) : null}

            <div className="mb-5 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
              <div className="h-1 bg-gradient-to-r from-[#2F5597] via-[#4472C4] to-sky-500" aria-hidden />
              <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <div className="px-5 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Actual cost ({subscriptionData.currency})
                  </span>
                  <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-slate-900">
                    {formatBillingCurrency(subscriptionData.actualCostMtd, subscriptionData.currency)}
                  </p>
                </div>
                <div className="px-5 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                    Forecast (month end)
                  </span>
                  <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-emerald-900">
                    {subscriptionData.forecastMonthEnd != null
                      ? formatBillingCurrency(subscriptionData.forecastMonthEnd, subscriptionData.currency)
                      : '—'}
                  </p>
                </div>
                <div className="px-5 py-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Period</span>
                  <p className="mt-1 text-lg font-semibold text-slate-800">{periodLabel}</p>
                  <p className="text-xs text-slate-500">Accumulated cost · area chart</p>
                </div>
              </div>
            </div>

            <MsrChartCapture
              id="msr-chart-azure-cost"
              chartTitle="Azure accumulated cost"
              downloadDisabled={subscription.isFetching || subscriptionData.dailySeries.length === 0}
            >
              <KpiChartPanel title="Azure accumulated cost" className="h-full" flexFill bodyClassName="p-3">
                <AzureCostAreaChart
                  dailySeries={subscriptionData.dailySeries}
                  currency={subscriptionData.currency}
                />
              </KpiChartPanel>
            </MsrChartCapture>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <KpiChartPanel title="Service name" bodyClassName="p-0">
                <div className="max-h-56 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <tbody className="divide-y divide-slate-50">
                      {subscriptionData.byService.length === 0 ? (
                        <tr>
                          <td className="px-3 py-4 text-center text-slate-500">No service breakdown.</td>
                        </tr>
                      ) : (
                        subscriptionData.byService.map((row) => (
                          <tr key={row.serviceName} className="hover:bg-slate-50/80">
                            <td className="px-3 py-2 text-slate-800">{row.serviceName}</td>
                            <td className="px-3 py-2 text-right font-medium text-slate-900">
                              {formatBillingCurrency(row.cost, subscriptionData.currency)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </KpiChartPanel>

              <KpiChartPanel title="Engagements (subscriptions)" bodyClassName="p-0">
                <div className="max-h-56 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <tbody className="divide-y divide-slate-50">
                      {subscriptionData.engagements.map((eng) => (
                        <tr key={eng.engagementId} className="hover:bg-slate-50/80">
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-800">{eng.engagementName}</div>
                            {eng.error ? (
                              <div className="text-xs text-red-600">{eng.error}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-slate-900">
                            {eng.error
                              ? '—'
                              : formatBillingCurrency(eng.actualCostMtd, eng.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </KpiChartPanel>

              {subscriptionData.skippedEngagements?.length ? (
                <div className="lg:col-span-2">
                  <KpiChartPanel title="Excluded engagements" bodyClassName="p-0">
                    <div className="max-h-40 overflow-y-auto">
                      <table className="min-w-full text-sm">
                        <tbody className="divide-y divide-slate-100">
                          {subscriptionData.skippedEngagements.map((row) => (
                            <tr key={row.engagementId}>
                              <td className="px-3 py-2 text-slate-700">{row.engagementName}</td>
                              <td className="px-3 py-2 text-right text-xs text-slate-500">{row.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </KpiChartPanel>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </MsrReportSlide>

      <MsrReportSlide
        id="msr-slide-azure-cost-3months"
        title="Azure cost report for last 3 months"
        onExportPdf={threeMonthExport.onExportPdf}
        onExportExcel={threeMonthExport.onExportExcel}
        exportDisabled={last3MonthsAnalysis.isFetching || !hasAnalysisData}
      >
        {last3MonthsAnalysis.isFetching && hasAnalysisData ? (
          <div
            className="mb-3 flex items-center gap-1.5 text-xs font-medium text-indigo-800"
            aria-live="polite"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Updating 3-month report
          </div>
        ) : null}

        {last3MonthsAnalysis.isLoading ? (
          <div className="space-y-4">
            <ChartSkeleton />
            <div className="h-28 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80" />
          </div>
        ) : last3MonthsAnalysis.error && !hasAnalysisData ? (
          <SectionError
            title="Azure 3-month report unavailable"
            message={last3MonthsAnalysis.error.message}
            onRetry={last3MonthsAnalysis.onRetry}
          />
        ) : analysisData ? (
          <>
            <MsrChartCapture
              id="msr-chart-azure-cost-3months"
              chartTitle="Azure Cost Analysis Report"
              downloadDisabled={
                last3MonthsAnalysis.isFetching || analysisData.last3Months.length === 0
              }
            >
              <KpiChartPanel title="Azure Cost Analysis Report" className="h-full" flexFill bodyClassName="p-3">
                <AzureCost3MonthBarChart
                  months={analysisData.last3Months}
                  currency={analysisData.currency}
                />
              </KpiChartPanel>
            </MsrChartCapture>
            <MsrObservationBox observations={last3MonthObservations} />
          </>
        ) : null}
      </MsrReportSlide>

      <MsrReportSlide
        id="msr-slide-azure-cost-3months-analysis"
        title="Azure cost analysis for last 3 months"
        onExportPdf={analysisExport.onExportPdf}
        onExportExcel={analysisExport.onExportExcel}
        exportDisabled={last3MonthsAnalysis.isFetching || !hasAnalysisData}
      >
        {last3MonthsAnalysis.isFetching && hasAnalysisData ? (
          <div
            className="mb-3 flex items-center gap-1.5 text-xs font-medium text-indigo-800"
            aria-live="polite"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Updating cost analysis table
          </div>
        ) : null}

        {last3MonthsAnalysis.isLoading ? (
          <ChartSkeleton height={256} />
        ) : last3MonthsAnalysis.error && !hasAnalysisData ? (
          <SectionError
            title="Azure cost analysis unavailable"
            message={last3MonthsAnalysis.error.message}
            onRetry={last3MonthsAnalysis.onRetry}
          />
        ) : analysisData ? (
          <KpiChartPanel title="Service cost analysis (3 months)" bodyClassName="p-0">
            <AzureCost3MonthAnalysisTable
              rows={analysisData.serviceAnalysis3Months}
              currency={analysisData.currency}
            />
          </KpiChartPanel>
        ) : null}
      </MsrReportSlide>
    </>
  );
};

export default MsrAzureCostSection;
